import React, { useMemo } from 'react';
import { Card, CardContent, Chip, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { chargingAtom, BuildingIDProps } from '../atoms/chargers';
import { useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { listDevices } from '../handlers/plugCommissionHandler';

interface ChargingCardProps extends BuildingIDProps {
  /** When provided, show building selector in place of title */
  buildingOptions?: string[];
  buildingOptionsLoading?: boolean;
  onBuildingChange?: (id: string) => void;
}

export const ChargingCard: React.FC<ChargingCardProps> = ({
  BuildingID,
  buildingOptions,
  buildingOptionsLoading,
  onBuildingChange,
}) => {
  const [chargingData] = useAtom(chargingAtom);

  const { data: matterDevices = [] } = useQuery({
    queryKey: ['fpmMatterDevices', BuildingID],
    queryFn: () => listDevices(BuildingID || 'all'),
    enabled: !!BuildingID,
  });

  const availablePlugCount = useMemo(
    () => matterDevices.filter((d) => d.Available !== 0).length,
    [matterDevices]
  );

  if (BuildingID === undefined && !buildingOptions) {
    return null;
  }

  const buildingKey = (BuildingID || '').toLowerCase();
  const data = chargingData[buildingKey];

  const showSelector = buildingOptions && onBuildingChange;

  if (!data && !showSelector) {
    return <Typography>Loading charging data for {BuildingID}...</Typography>;
  }

  const statsRow = data && (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body2">Total Plugs</Typography>
        <Chip
          size="small"
          label={data.NChans}
          sx={{ backgroundColor: 'secondary.light', color: 'primary.contrastText' }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body2">Avail Plugs</Typography>
        <Chip
          size="small"
          label={availablePlugCount}
          sx={{ backgroundColor: 'secondary.light', color: 'primary.contrastText' }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body2">Charging</Typography>
        <Chip
          size="small"
          label={data.NCharging}
          sx={{ backgroundColor: 'secondary.light', color: 'primary.contrastText' }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body2">Waiting</Typography>
        <Chip
          size="small"
          label={data.NWaiting}
          sx={{ backgroundColor: 'secondary.light', color: 'primary.contrastText' }}
        />
      </div>
    </div>
  );

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', height: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
          {showSelector ? (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="fpm-chargers-building-label">Building</InputLabel>
              <Select
                labelId="fpm-chargers-building-label"
                value={BuildingID || ''}
                label="Building"
                onChange={(e) => onBuildingChange(e.target.value)}
                displayEmpty
                renderValue={(v) => v || 'Select a building'}
              >
                {buildingOptionsLoading ? (
                  <MenuItem value="" disabled>Loading…</MenuItem>
                ) : !buildingOptions?.length ? (
                  <MenuItem value=""><em>Select a building</em></MenuItem>
                ) : (
                  buildingOptions.map((id) => (
                    <MenuItem key={id} value={id}>{id}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="h6" component="div" sx={{ color: 'text.primary' }}>
              {BuildingID}
            </Typography>
          )}
          {statsRow}
        </div>
      </CardContent>
    </Card>
  );
};

export default ChargingCard;
